import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { isDealerUser } from "../lib/roles";
import DealJacketTab from "../components/crm/DealJacketTab";

const TABS = [
  { k: "overview",   label: "Overview" },
  { k: "personal",   label: "Personal Info" },
  { k: "contact",    label: "Contact Info" },
  { k: "license",    label: "Driver's License" },
  { k: "insurance",  label: "Insurance" },
  { k: "interest",   label: "Vehicle of Interest" },
  { k: "purchase",   label: "Purchase Vehicle" },
  { k: "trade",      label: "Trade-In" },
  { k: "finance",    label: "Finance / Payoff" },
  { k: "protection", label: "Protection Products" },
  { k: "deal_jacket", label: "Deal Jacket" },
  { k: "uploads",    label: "Uploaded Documents" },
  { k: "forms",      label: "Quick Forms" },
  { k: "notes",      label: "Notes" },
  { k: "tasks",      label: "Tasks" },
  { k: "appts",      label: "Appointments" },
  { k: "messages",   label: "Messages" },
  { k: "activity",   label: "Activity Timeline" },
  { k: "info",       label: "All Fields" },
];

// Map a missing-item label (returned by backend) to the tab it should jump to.
const MISSING_TO_TAB = [
  { match: /license/i,        tab: "uploads" },
  { match: /insurance/i,      tab: "uploads" },
  { match: /title/i,          tab: "uploads" },
  { match: /payoff/i,         tab: "finance" },
  { match: /lienholder/i,     tab: "finance" },
  { match: /trade.*vin|trade.*mileage|trade vin/i, tab: "trade" },
  { match: /purchase.*vin|purchase vin/i, tab: "purchase" },
  { match: /vin/i,            tab: "purchase" },
  { match: /appointment/i,    tab: "appts" },
  { match: /task|follow.?up/i, tab: "tasks" },
  { match: /phone|email|address/i, tab: "info" },
];
function _missingToTab(label) {
  for (const m of MISSING_TO_TAB) if (m.match.test(label)) return m.tab;
  return "info";
}

export default function AdminCustomerProfile() {
  const { id } = useParams();
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [forms, setForms] = useState([]);

  useEffect(() => { if (ready && !isDealerUser(user)) navigate("/dealer-login"); }, [ready, user, navigate]);

  const reload = async () => {
    const { data } = await api.get(`/admin/crm/customer/${id}`);
    setData(data);
  };
  useEffect(() => { if (isDealerUser(user)) reload(); /* eslint-disable-next-line */ }, [id, user]);
  useEffect(() => { api.get("/admin/crm/forms").then(r => setForms(r.data.forms || [])).catch(() => {}); }, []);

  if (!data) {
    return <div className="ssc-container ssc-section" data-testid="admin-customer-loading">Loading customer…</div>;
  }
  const c = data.customer || {};
  const fullName = [c.first_name, c.middle_name, c.last_name].filter(Boolean).join(" ") || c.name || "Customer";

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="admin-customer-profile">
      {/* Lovable-style command-center header */}
      <div className="bg-white border-b border-gray-200">
        <div className="ssc-container py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Link to="/admin/crm" className="text-xs text-blue-600 hover:underline">← Customers</Link>
              <div className="flex items-baseline gap-3 mt-1 flex-wrap">
                <h1 className="text-3xl font-black text-[#061426]" data-testid="admin-customer-name">{fullName}</h1>
                {c.status && <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${c.status === "sold" ? "bg-emerald-100 text-emerald-800" : c.status === "lost" ? "bg-gray-200 text-gray-700" : "bg-blue-100 text-blue-800"}`}>{c.status}</span>}
                {c.deal_code && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-violet-100 text-violet-800">{c.deal_code}</span>}
              </div>
              <div className="text-sm text-gray-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                {c.phone && <span><span className="text-gray-400">📞</span> <a href={`tel:${c.phone}`} className="text-blue-600 hover:underline font-semibold">{c.phone}</a></span>}
                {c.email && <span><span className="text-gray-400">✉</span> <a href={`mailto:${c.email}`} className="text-blue-600 hover:underline">{c.email}</a></span>}
                {c.lead_source && <span><span className="text-gray-400">Source:</span> {c.lead_source}</span>}
                {c.assigned_to_name && <span><span className="text-gray-400">Assigned:</span> <strong className="text-[#061426]">{c.assigned_to_name}</strong></span>}
                {c.last_contacted_at && <span><span className="text-gray-400">Last touch:</span> {String(c.last_contacted_at).slice(0, 10)}</span>}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap shrink-0">
              {c.phone && <a href={`tel:${c.phone}`} className="text-xs font-bold px-3 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-500" data-testid="hdr-call">📞 Call</a>}
              {c.phone && <a href={`sms:${c.phone}`} className="text-xs font-bold px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-500" data-testid="hdr-text">💬 Text</a>}
              {c.email && <a href={`mailto:${c.email}`} className="text-xs font-bold px-3 py-2 rounded bg-white border border-gray-300 hover:bg-gray-50" data-testid="hdr-email">✉ Email</a>}
              <button onClick={() => setTab("appts")} className="text-xs font-bold px-3 py-2 rounded bg-white border border-gray-300 hover:bg-gray-50" data-testid="hdr-appt">📆 Appointment</button>
              <button onClick={() => setTab("deal_jacket")} className="text-xs font-bold px-3 py-2 rounded bg-violet-600 text-white hover:bg-violet-500" data-testid="hdr-deal-jacket">📁 Deal Jacket</button>
              <button onClick={() => setTab("forms")} className="text-xs font-bold px-3 py-2 rounded bg-white border border-gray-300 hover:bg-gray-50" data-testid="hdr-print">🖨 Print Paperwork</button>
            </div>
          </div>
        </div>
        <div className="ssc-container">
          <div className="flex flex-nowrap gap-1 overflow-x-auto snap-x snap-mandatory scrollbar-thin [scrollbar-width:thin]" data-testid="admin-customer-tabs">
            {TABS.map(t => (
              <button key={t.k}
                className={`snap-start px-3 py-2 text-sm whitespace-nowrap border-b-2 transition ${tab === t.k ? "border-[#006DFF] font-semibold text-[#006DFF]" : "border-transparent text-gray-600 hover:text-[#061426]"}`}
                onClick={() => setTab(t.k)} data-testid={`profile-tab-${t.k}`}>{t.label}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="ssc-container py-4">
      {data.missing_items && data.missing_items.length > 0 ? (
        <div className="bg-amber-50 border border-amber-300 rounded p-3 mb-3 text-sm flex items-start gap-2" data-testid="admin-customer-missing">
          <span className="font-semibold text-amber-800 shrink-0">Still needed:</span>
          <div className="flex flex-wrap gap-1">
            {data.missing_items.map((item, idx) => (
              <button key={idx}
                onClick={() => setTab(_missingToTab(item))}
                className="text-xs px-2 py-0.5 rounded bg-white border border-amber-300 text-amber-800 hover:bg-amber-100"
                data-testid={`missing-jump-${idx}`}>{item}</button>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-emerald-50 border border-emerald-300 rounded p-2 mb-3 text-xs text-emerald-800" data-testid="admin-customer-complete">
          ✓ Deal file is complete — nothing missing.
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        {tab === "overview"   && <OverviewTab data={data} setTab={setTab} />}
        {tab === "personal"   && <CustomerInfoTab data={data} reload={reload} />}
        {tab === "contact"    && <CustomerInfoTab data={data} reload={reload} />}
        {tab === "license"    && <UploadsTab data={data} reload={reload} defaultKind="license_front" filterPrefix="license" />}
        {tab === "insurance"  && <UploadsTab data={data} reload={reload} defaultKind="insurance" filterPrefix="insurance" />}
        {tab === "messages"   && <MessagesTab data={data} customerId={c.id} />}
        {tab === "info"       && <CustomerInfoTab data={data} reload={reload} />}
        {tab === "interest"   && <VehicleTab data={data} reload={reload} field="vehicle_of_interest" label="Vehicle of Interest" />}
        {tab === "purchase"   && <VehicleTab data={data} reload={reload} field="purchase_vehicle"    label="Purchase Vehicle" />}
        {tab === "trade"      && <VehicleTab data={data} reload={reload} field="trade_vehicle"        label="Trade-In Vehicle" extraTrade />}
        {tab === "finance"    && <FinanceTab data={data} reload={reload} />}
        {tab === "appts"      && <AppointmentsTab data={data} reload={reload} />}
        {tab === "tasks"      && <TasksTab data={data} reload={reload} />}
        {tab === "notes"      && <NotesTab data={data} reload={reload} />}
        {tab === "deal_jacket" && <DealJacketTab customerId={c.id} />}
        {tab === "uploads"    && <UploadsTab data={data} reload={reload} />}
        {tab === "protection" && <ProtectionTab data={data} />}
        {tab === "forms"      && <FormsTab data={data} forms={forms} />}
        {tab === "activity"   && <ActivityTab data={data} />}
      </div>
      </div>
    </div>
  );
}

/* Messages tab — links to the unified Conversations inbox filtered by customer.
 * Backend already powers /admin/conversations; this is a thin shortcut. */
function MessagesTab({ data, customerId }) {
  const c = data.customer || {};
  return (
    <div className="space-y-3" data-testid="messages-tab">
      <p className="text-sm text-gray-600">Calls, texts, and emails for this customer thread.</p>
      <div className="flex flex-wrap gap-2">
        {c.phone && <a href={`tel:${c.phone}`} className="ssc-btn-primary text-sm" data-testid="msg-call">📞 Call {c.phone}</a>}
        {c.phone && <a href={`sms:${c.phone}`} className="ssc-btn-secondary text-sm" data-testid="msg-text">💬 Text {c.phone}</a>}
        {c.email && <a href={`mailto:${c.email}`} className="ssc-btn-secondary text-sm" data-testid="msg-email">✉ Email {c.email}</a>}
      </div>
      <Link to={`/admin/conversations?customer=${customerId}`} className="inline-block text-xs text-blue-600 hover:underline" data-testid="msg-open-inbox">
        Open full conversation history in Messages →
      </Link>
    </div>
  );
}

// =============================================================================
// Tabs
// =============================================================================
function OverviewTab({ data, setTab }) {
  const c = data.customer || {};
  const pv = c.purchase_vehicle || {}, tv = c.trade_vehicle || {}, fi = c.finance_info || {};

  // Compute "next action" signals
  const tasks = data.tasks || [];
  const appts = data.appointments || [];
  const now = Date.now();
  const overdueTasks = tasks.filter(t => t.status !== "Completed" && t.due_at && new Date(t.due_at).getTime() < now);
  const todayTasks = tasks.filter(t => t.status !== "Completed" && t.due_at && new Date(t.due_at).toDateString() === new Date().toDateString());
  const nextTask = tasks.filter(t => t.status !== "Completed").sort((a, b) => new Date(a.due_at || 0) - new Date(b.due_at || 0))[0];
  const nextAppt = appts.filter(a => a.status !== "Canceled" && a.status !== "Completed" && a.starts_at && new Date(a.starts_at).getTime() >= now).sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at))[0];
  const lastNote = (data.notes || [])[0];
  const uploads = data.uploads || [];
  const has = (k) => uploads.some(u => u.kind === k);

  const recentActivity = (data.activity || []).slice(0, 4);

  const goto = (k) => setTab && setTab(k);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-tab-body-overview">
      <Box title="Snapshot">
        <KV label="Status" value={c.status} />
        <KV label="Temperature" value={c.temperature} />
        <KV label="Source" value={c.lead_source} />
        <KV label="Created" value={(c.created_at || "").slice(0, 16).replace("T", " ")} />
        <KV label="Last Touch" value={(c.last_touch_at || "").slice(0, 16).replace("T", " ")} />
        <KV label="Assigned" value={c.assigned_to_name} />
        {c.ai_intent_score && <KV label="AI Intent" value={c.ai_intent_score} />}
      </Box>

      <Box title="Next Action">
        {overdueTasks.length > 0 && (
          <button onClick={() => goto("tasks")} className="block w-full text-left text-xs text-red-700 font-semibold mb-1 hover:underline" data-testid="overview-overdue">
            ⚠ {overdueTasks.length} overdue task{overdueTasks.length === 1 ? "" : "s"}
          </button>
        )}
        <KV label="Next Task" value={nextTask ? `${nextTask.title}${nextTask.due_at ? ` · ${nextTask.due_at.replace("T", " ").slice(0, 16)}` : ""}` : "—"} />
        <KV label="Tasks Today" value={todayTasks.length || "0"} />
        <KV label="Next Appt" value={nextAppt ? `${nextAppt.appt_type} · ${(nextAppt.starts_at || "").replace("T", " ").slice(0, 16)}` : "—"} />
        <div className="flex gap-2 mt-2">
          <button onClick={() => goto("tasks")} className="text-xs text-blue-600 hover:underline" data-testid="overview-go-tasks">+ Task</button>
          <button onClick={() => goto("appts")} className="text-xs text-blue-600 hover:underline" data-testid="overview-go-appts">+ Appointment</button>
        </div>
      </Box>

      <Box title="Vehicle Interest">
        <KV label="Of Interest" value={c.vehicle_interest || c.vehicle_title} />
        <KV label="Purchasing" value={[pv.year, pv.make, pv.model, pv.trim].filter(Boolean).join(" ")} />
        <KV label="Stock #" value={pv.stock_number || c.stock_number} />
        <KV label="VIN" value={pv.vin || c.vin} />
        <KV label="Condition" value={pv.condition || c.condition} />
        <button onClick={() => goto("purchase")} className="text-xs text-blue-600 hover:underline mt-1" data-testid="overview-go-purchase">Edit purchase →</button>
      </Box>

      <Box title="Trade & Finance">
        <KV label="Has Trade?" value={tv && tv.vin ? "Yes" : "No"} />
        <KV label="Trade" value={[tv.year, tv.make, tv.model].filter(Boolean).join(" ") || "—"} />
        <KV label="Trade VIN" value={tv.vin} />
        <KV label="Mileage" value={tv.mileage} />
        <KV label="Payoff" value={fi.payoff_amount} />
        <KV label="Lienholder" value={fi.payoff_lender} />
        <KV label="Down" value={fi.down_payment} />
        <KV label="Finance" value={fi.deal_type} />
      </Box>

      <Box title="Paperwork">
        <KV label="License" value={has("license_front") || has("license_back") ? "✓ On file" : "Missing"} />
        <KV label="Insurance" value={has("insurance") ? "✓ On file" : "Missing"} />
        <KV label="Trade Title" value={has("title") ? "✓ On file" : (tv && tv.vin ? "Missing" : "N/A")} />
        <KV label="Payoff Letter" value={has("payoff_letter") ? "✓ On file" : (tv && tv.vin ? "Missing" : "N/A")} />
        <KV label="Uploads Total" value={uploads.length} />
        <button onClick={() => goto("uploads")} className="text-xs text-blue-600 hover:underline mt-1" data-testid="overview-go-uploads">Open Uploaded Docs →</button>
        <button onClick={() => goto("deal_jacket")} className="block text-xs text-blue-600 hover:underline mt-1" data-testid="overview-go-dj">Open Deal Jacket →</button>
      </Box>

      <Box title="Recent Activity">
        {lastNote && <div className="text-xs text-gray-700 mb-2 border-l-2 border-blue-300 pl-2"><strong>Last note:</strong> {lastNote.text?.slice(0, 80)}{(lastNote.text || "").length > 80 ? "…" : ""}</div>}
        {recentActivity.length === 0 && <div className="text-xs text-gray-400">No activity yet.</div>}
        <ul className="space-y-1">
          {recentActivity.map((a, i) => (
            <li key={i} className="text-xs">
              <span className="font-semibold text-[#061426]">{a.action}</span>
              {a.detail ? <span className="text-gray-600"> · {a.detail.slice(0, 50)}</span> : null}
              <div className="text-[10px] text-gray-400">{(a.at || "").slice(0, 16).replace("T", " ")}</div>
            </li>
          ))}
        </ul>
        <button onClick={() => goto("activity")} className="text-xs text-blue-600 hover:underline mt-2" data-testid="overview-go-activity">See full timeline →</button>
      </Box>
    </div>
  );
}

function CustomerInfoTab({ data, reload }) {
  const c = data.customer || {};
  const [form, setForm] = useState({
    first_name: c.first_name || "", middle_name: c.middle_name || "", last_name: c.last_name || "",
    dob: c.dob || "", phone: c.phone || "", work_phone: c.work_phone || "",
    email: c.email || "", preferred_contact: c.preferred_contact || "",
    address: c.address || "", city: c.city || "", state: c.state || "", zip: c.zip || "",
    driver_license_number: c.driver_license_number || "", driver_license_state: c.driver_license_state || "",
    driver_license_exp: c.driver_license_exp || "",
    insurance_company: c.insurance_company || "", insurance_agent: c.insurance_agent || "",
    insurance_policy_number: c.insurance_policy_number || "",
    insurance_effective: c.insurance_effective || "", insurance_expiration: c.insurance_expiration || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/crm/customer/${c.id}`, { customer_info: form });
      await reload();
    } finally { setSaving(false); }
  };
  const F = (k, label, type = "text") => (
    <Field label={label}>
      <input type={type} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className="w-full border border-gray-300 rounded p-2 text-sm" data-testid={`info-${k}`} />
    </Field>
  );
  return (
    <div data-testid="admin-tab-body-info">
      <Section title="Personal" cols={4}>{F("first_name", "First Name")}{F("middle_name", "Middle")}{F("last_name", "Last Name")}{F("dob", "Date of Birth")}</Section>
      <Section title="Contact" cols={4}>{F("phone", "Phone")}{F("work_phone", "Work Phone")}{F("email", "Email")}<Field label="Preferred Contact">
        <select value={form.preferred_contact || ""} onChange={(e) => setForm({ ...form, preferred_contact: e.target.value })} className="w-full border border-gray-300 rounded p-2 text-sm">
          <option value="">—</option><option>Text</option><option>Call</option><option>Email</option>
        </select></Field></Section>
      <Section title="Address" cols={4}>{F("address", "Street")}{F("city", "City")}{F("state", "State")}{F("zip", "ZIP")}</Section>
      <Section title="Driver License" cols={3}>{F("driver_license_number", "DL Number")}{F("driver_license_state", "DL State")}{F("driver_license_exp", "DL Expiration")}</Section>
      <Section title="Insurance" cols={3}>{F("insurance_company", "Carrier")}{F("insurance_agent", "Agent")}{F("insurance_policy_number", "Policy #")}{F("insurance_effective", "Effective")}{F("insurance_expiration", "Expiration")}</Section>
      <div className="flex justify-end mt-3">
        <button className="ssc-btn-primary" onClick={save} data-testid="info-save">{saving ? "Saving…" : "Save Customer Info"}</button>
      </div>
    </div>
  );
}

function VehicleTab({ data, reload, field, label, extraTrade }) {
  const c = data.customer || {};
  const v = c[field] || {};
  const [form, setForm] = useState({
    year: v.year || "", make: v.make || "", model: v.model || "", trim: v.trim || "",
    vin: v.vin || "", stock_number: v.stock_number || "", mileage: v.mileage || "",
    body_style: v.body_style || "", exterior_color: v.exterior_color || "", interior_color: v.interior_color || "",
    msrp: v.msrp || "", sale_price: v.sale_price || "",
    ...(extraTrade ? {
      title_status: v.title_status || "", owners: v.owners || "", accidents: v.accidents || "",
      condition: v.condition || "", acv: v.acv || "", trade_allowance: v.trade_allowance || "", net_equity: v.net_equity || "",
    } : {}),
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/crm/customer/${c.id}`, { [field]: form });
      await reload();
    } finally { setSaving(false); }
  };
  const F = (k, lab) => (
    <Field label={lab}><input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
      className="w-full border border-gray-300 rounded p-2 text-sm" data-testid={`${field}-${k}`} /></Field>
  );
  return (
    <div data-testid={`admin-tab-body-${field}`}>
      <h3 className="font-bold mb-2">{label}</h3>
      <Section cols={4}>{F("year", "Year")}{F("make", "Make")}{F("model", "Model")}{F("trim", "Trim")}</Section>
      <Section cols={4}>{F("vin", "VIN")}{F("stock_number", "Stock #")}{F("mileage", "Mileage")}{F("body_style", "Body Style")}</Section>
      <Section cols={4}>{F("exterior_color", "Ext. Color")}{F("interior_color", "Int. Color")}{F("msrp", "MSRP")}{F("sale_price", "Sale / Allowance")}</Section>
      {extraTrade && (
        <>
          <Section cols={4}>{F("title_status", "Title Status")}{F("owners", "# Owners")}{F("accidents", "# Accidents")}{F("condition", "Condition")}</Section>
          <Section cols={3}>{F("acv", "ACV / Wholesale")}{F("trade_allowance", "Trade Allowance")}{F("net_equity", "Net Equity")}</Section>
        </>
      )}
      <div className="flex justify-end mt-3">
        <button className="ssc-btn-primary" onClick={save} data-testid={`${field}-save`}>{saving ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}

function FinanceTab({ data, reload }) {
  const c = data.customer || {};
  const fi = c.finance_info || {};
  const [form, setForm] = useState({
    deal_type: fi.deal_type || "Finance",
    lender: fi.lender || "", apr: fi.apr || "", term_months: fi.term_months || "",
    down_payment: fi.down_payment || "", monthly_payment: fi.monthly_payment || "",
    payoff_lender: fi.payoff_lender || "", payoff_lender_phone: fi.payoff_lender_phone || "",
    payoff_account: fi.payoff_account || "", payoff_amount: fi.payoff_amount || "",
    payoff_good_through: fi.payoff_good_through || "", payoff_per_diem: fi.payoff_per_diem || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/admin/crm/customer/${c.id}`, { finance_info: form }); await reload(); }
    finally { setSaving(false); }
  };
  const F = (k, lab) => (
    <Field label={lab}><input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
      className="w-full border border-gray-300 rounded p-2 text-sm" data-testid={`fin-${k}`} /></Field>
  );
  return (
    <div data-testid="admin-tab-body-finance">
      <Section title="Deal Terms" cols={4}>
        <Field label="Deal Type">
          <select value={form.deal_type} onChange={(e) => setForm({ ...form, deal_type: e.target.value })} className="w-full border border-gray-300 rounded p-2 text-sm">
            <option>Finance</option><option>Lease</option><option>Cash</option>
          </select>
        </Field>
        {F("lender", "Lender")}{F("apr", "APR %")}{F("term_months", "Term (months)")}
      </Section>
      <Section cols={3}>{F("down_payment", "Down Payment")}{F("monthly_payment", "Monthly Payment")}</Section>
      <Section title="Trade Payoff" cols={3}>
        {F("payoff_lender", "Lender")}{F("payoff_lender_phone", "Lender Phone")}{F("payoff_account", "Account #")}
        {F("payoff_amount", "Payoff Amount")}{F("payoff_good_through", "Good-through Date")}{F("payoff_per_diem", "Per Diem")}
      </Section>
      <div className="flex justify-end mt-3"><button className="ssc-btn-primary" onClick={save} data-testid="fin-save">{saving ? "Saving…" : "Save"}</button></div>
    </div>
  );
}

function AppointmentsTab({ data, reload }) {
  const c = data.customer || {};
  const [form, setForm] = useState({ appt_type: "Test Drive", starts_at: "", duration_min: 30, location: "Byers Chevrolet", notes: "" });
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!form.starts_at) return;
    setBusy(true);
    try { await api.post("/admin/crm/appointments", { customer_id: c.id, ...form }); await reload(); setForm({ ...form, starts_at: "", notes: "" }); }
    finally { setBusy(false); }
  };
  const setStatus = async (id, status) => {
    await api.patch(`/admin/crm/appointments/${id}`, { status });
    await reload();
  };
  return (
    <div data-testid="admin-tab-body-appts">
      <div className="bg-gray-50 border rounded p-3 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <Field label="Type">
            <select value={form.appt_type} onChange={(e) => setForm({ ...form, appt_type: e.target.value })} className="w-full border border-gray-300 rounded p-2 text-sm">
              <option>Test Drive</option><option>Showroom Visit</option><option>Phone Call</option><option>Video Walkaround</option><option>Delivery</option>
            </select>
          </Field>
          <Field label="When"><input type="datetime-local" value={form.starts_at}
            onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="w-full border border-gray-300 rounded p-2 text-sm" data-testid="appt-when" /></Field>
          <Field label="Duration (min)"><input type="number" value={form.duration_min}
            onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value || 30) })} className="w-full border border-gray-300 rounded p-2 text-sm" /></Field>
          <Field label="Location"><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full border border-gray-300 rounded p-2 text-sm" /></Field>
          <Field label="Notes" wide><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-gray-300 rounded p-2 text-sm" /></Field>
        </div>
        <div className="flex justify-end mt-2"><button className="ssc-btn-primary" onClick={create} disabled={busy} data-testid="appt-create">{busy ? "Saving…" : "+ Schedule"}</button></div>
      </div>
      <ul className="divide-y" data-testid="appt-list">
        {(data.appointments || []).map(a => (
          <li key={a.id} className="py-2 flex items-center justify-between gap-2">
            <div>
              <div className="font-semibold">{a.appt_type} <span className="text-gray-400">·</span> <span className="font-normal">{(a.starts_at || "").replace("T", " ").slice(0, 16)}</span></div>
              <div className="text-xs text-gray-500">{a.location || ""}{a.notes ? ` · ${a.notes}` : ""}</div>
            </div>
            <div className="flex gap-2 items-center">
              <span className="text-xs px-2 py-0.5 rounded bg-gray-100">{a.status}</span>
              <button className="text-xs text-blue-600 hover:underline" onClick={() => setStatus(a.id, "Confirmed")}>Confirm</button>
              <button className="text-xs text-emerald-700 hover:underline" onClick={() => setStatus(a.id, "Completed")}>Completed</button>
              <button className="text-xs text-red-600 hover:underline" onClick={() => setStatus(a.id, "Canceled")}>Cancel</button>
            </div>
          </li>
        ))}
        {(data.appointments || []).length === 0 && <li className="py-4 text-gray-400 text-center">No appointments yet.</li>}
      </ul>
    </div>
  );
}

function TasksTab({ data, reload }) {
  const c = data.customer || {};
  const [form, setForm] = useState({ title: "", task_type: "Call", due_at: "", priority: "Normal", notes: "" });
  const create = async () => {
    if (!form.title) return;
    await api.post("/admin/crm/tasks", { customer_id: c.id, ...form });
    setForm({ title: "", task_type: "Call", due_at: "", priority: "Normal", notes: "" });
    reload();
  };
  const setStatus = async (id, status) => { await api.patch(`/admin/crm/tasks/${id}`, { status }); reload(); };
  return (
    <div data-testid="admin-tab-body-tasks">
      <div className="bg-gray-50 border rounded p-3 mb-3 grid grid-cols-1 md:grid-cols-6 gap-2">
        <Field label="Title" wide><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="w-full border border-gray-300 rounded p-2 text-sm" data-testid="task-title-input" placeholder="Call back about trade payoff" /></Field>
        <Field label="Type">
          <select value={form.task_type} onChange={(e) => setForm({ ...form, task_type: e.target.value })} className="w-full border border-gray-300 rounded p-2 text-sm">
            <option>Call</option><option>Text</option><option>Email</option><option>Follow Up</option><option>Paperwork</option><option>Other</option>
          </select>
        </Field>
        <Field label="Due"><input type="datetime-local" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} className="w-full border border-gray-300 rounded p-2 text-sm" /></Field>
        <Field label="Priority">
          <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full border border-gray-300 rounded p-2 text-sm">
            <option>Low</option><option>Normal</option><option>High</option><option>Urgent</option>
          </select>
        </Field>
        <div className="flex items-end"><button className="ssc-btn-primary w-full" onClick={create} data-testid="task-create">+ Add Task</button></div>
      </div>
      <ul className="divide-y" data-testid="task-list">
        {(data.tasks || []).map(t => (
          <li key={t.id} className="py-2 flex items-center justify-between gap-2">
            <div>
              <div className="font-semibold">{t.title} <span className="text-xs text-gray-400 ml-1">[{t.task_type}]</span></div>
              <div className="text-xs text-gray-500">{t.due_at ? `Due ${t.due_at.replace("T", " ").slice(0, 16)}` : "No due date"} · {t.priority}</div>
            </div>
            <div className="flex gap-2 items-center">
              <span className="text-xs px-2 py-0.5 rounded bg-gray-100">{t.status}</span>
              <button className="text-xs text-emerald-700 hover:underline" onClick={() => setStatus(t.id, "Completed")}>Done</button>
              <button className="text-xs text-amber-700 hover:underline" onClick={() => setStatus(t.id, "Snoozed")}>Snooze</button>
            </div>
          </li>
        ))}
        {(data.tasks || []).length === 0 && <li className="py-4 text-gray-400 text-center">No tasks yet.</li>}
      </ul>
    </div>
  );
}

function NotesTab({ data, reload }) {
  const c = data.customer || {};
  const [text, setText] = useState("");
  const add = async () => {
    if (!text.trim()) return;
    await api.post("/admin/crm/notes", { customer_id: c.id, text });
    setText(""); reload();
  };
  return (
    <div data-testid="admin-tab-body-notes">
      <div className="flex gap-2 mb-3">
        <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a quick note (only Scott + admin see this)…"
          className="flex-1 border border-gray-300 rounded p-2 text-sm" rows={2} data-testid="note-text" />
        <button className="ssc-btn-primary self-start" onClick={add} data-testid="note-add">Add Note</button>
      </div>
      <ul className="divide-y" data-testid="note-list">
        {(data.notes || []).map(n => (
          <li key={n.id} className="py-2">
            <div className="text-sm">{n.text}</div>
            <div className="text-xs text-gray-400 mt-0.5">{n.user_name || ""} · {(n.at || "").slice(0, 16).replace("T", " ")}</div>
          </li>
        ))}
        {(data.notes || []).length === 0 && <li className="py-4 text-gray-400 text-center">No notes yet.</li>}
      </ul>
    </div>
  );
}

const DOC_KINDS = [
  { v: "license_front", label: "Driver License — Front", ocr: true },
  { v: "license_back",  label: "Driver License — Back",  ocr: true },
  { v: "insurance",     label: "Insurance Card",         ocr: true },
  { v: "insurance_back", label: "Insurance Card — Back", ocr: true },
  { v: "title",         label: "Title (Trade)",          ocr: true },
  { v: "registration",  label: "Registration",           ocr: false },
  { v: "payoff_letter", label: "Payoff Letter",          ocr: false },
  { v: "trade_photo",   label: "Trade Photo",            ocr: false },
  { v: "purchase_photo", label: "Purchase Photo",        ocr: false },
  { v: "income",        label: "Pay Stub / Income",      ocr: false },
  { v: "address_proof", label: "Proof of Address",       ocr: false },
  { v: "other",         label: "Other",                  ocr: false },
];

function UploadsTab({ data, reload, defaultKind, filterPrefix }) {
  const c = data.customer || {};
  const [kind, setKind] = useState(defaultKind || "license_front");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const fileRef = useRef(null);

  const supportsOcr = !!DOC_KINDS.find(k => k.v === kind && k.ocr);

  const upload = async (runOcr) => {
    if (!file) return;
    setBusy(true); setOcrResult(null);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("run_ocr", runOcr ? "true" : "false");
      fd.append("file", file);
      const { data: res } = await api.post(`/admin/crm/customer/${c.id}/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (res?.upload?.ocr_payload) setOcrResult({ upload: res.upload, payload: res.upload.ocr_payload });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await reload();
    } catch (e) {
      alert("Upload failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/admin/crm/documents/${id}`); reload();
  };

  const rerunOcr = async (id) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/admin/crm/documents/${id}/ocr`);
      setOcrResult({ upload: { id, kind: (data.uploads.find(u => u.id === id) || {}).kind }, payload: res.ocr });
    } finally { setBusy(false); }
  };

  const applyOcr = async () => {
    if (!ocrResult) return;
    const fields = { ...(ocrResult.payload || {}) };
    delete fields._ocr_provider; delete fields._ocr_error;
    await api.post(`/admin/crm/customer/${c.id}/apply-ocr`, { doc_id: ocrResult.upload.id, fields });
    setOcrResult(null); reload();
    alert("Customer profile updated from OCR.");
  };

  return (
    <div data-testid="admin-tab-body-uploads">
      <UploadLinkPanel customerId={c.id} />
      <div className="bg-gray-50 border rounded p-3 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
          <Field label="Document Kind">
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="w-full border border-gray-300 rounded p-2 text-sm" data-testid="upload-kind">
              {DOC_KINDS.map(k => <option key={k.v} value={k.v}>{k.label}</option>)}
            </select>
          </Field>
          <Field label="File" wide>
            <input ref={fileRef} type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm" data-testid="upload-file" />
          </Field>
          <div className="flex gap-2">
            <button className="ssc-btn-secondary" onClick={() => upload(false)} disabled={!file || busy} data-testid="upload-save">{busy ? "…" : "Upload"}</button>
            {supportsOcr && <button className="ssc-btn-primary" onClick={() => upload(true)} disabled={!file || busy} data-testid="upload-save-ocr">Upload + Run OCR</button>}
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">Files are stored privately — only authenticated CRM users can download them. JPG / PNG / WEBP / PDF up to 20MB.</p>
      </div>

      {ocrResult && (
        <div className="border border-emerald-300 bg-emerald-50 rounded p-3 mb-3" data-testid="ocr-review">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-emerald-800">OCR Extracted (review &amp; apply to customer profile)</div>
            <div className="text-xs text-emerald-700">Provider: {ocrResult.payload._ocr_provider || "—"}</div>
          </div>
          <pre className="text-xs whitespace-pre-wrap bg-white border rounded p-2 max-h-72 overflow-auto" data-testid="ocr-json">
            {JSON.stringify(ocrResult.payload, null, 2)}
          </pre>
          <div className="flex justify-end gap-2 mt-2">
            <button className="ssc-btn-secondary" onClick={() => setOcrResult(null)}>Dismiss</button>
            <button className="ssc-btn-primary" onClick={applyOcr} data-testid="ocr-apply">Apply to Customer Profile</button>
          </div>
        </div>
      )}

      <ul className="divide-y" data-testid="upload-list">
        {(data.uploads || []).filter(u => !filterPrefix || (u.kind || "").startsWith(filterPrefix)).map(u => (
          <li key={u.id} className="py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <DocThumb mime={u.mime} url={`${API}${u.url}`} />
              <div className="min-w-0">
                <div className="font-semibold truncate">{u.label} <span className="text-xs text-gray-400">· {u.kind}</span></div>
                <div className="text-xs text-gray-500 truncate">{u.filename} · {(u.uploaded_at || "").slice(0, 16).replace("T", " ")} · {u.uploaded_by_name || ""}</div>
                {u.ocr_status === "extracted" && <div className="text-xs text-emerald-700 mt-0.5">OCR extracted ({u.ocr_payload?._ocr_provider || "—"})</div>}
                {u.ocr_status === "failed" && <div className="text-xs text-red-600 mt-0.5">OCR failed</div>}
              </div>
            </div>
            <div className="flex gap-2 items-center shrink-0">
              <button className="text-xs text-blue-600 hover:underline" onClick={() => {
                const token = localStorage.getItem("ssc_token");
                fetch(`${API}${u.url}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
                  .then(r => r.blob()).then(b => { const x = URL.createObjectURL(b); window.open(x, "_blank"); });
              }} data-testid={`upload-view-${u.id}`}>View</button>
              {["license_front", "license_back", "insurance", "insurance_back", "title"].includes(u.kind) && (
                <button className="text-xs text-amber-700 hover:underline" onClick={() => rerunOcr(u.id)}>Re-run OCR</button>
              )}
              <button className="text-xs text-red-600 hover:underline" onClick={() => del(u.id)} data-testid={`upload-delete-${u.id}`}>Delete</button>
            </div>
          </li>
        ))}
        {(data.uploads || []).filter(u => !filterPrefix || (u.kind || "").startsWith(filterPrefix)).length === 0 && <li className="py-4 text-gray-400 text-center">No uploads yet.</li>}
      </ul>
    </div>
  );
}

function DocThumb({ mime, url }) {
  const [blob, setBlob] = useState(null);
  useEffect(() => {
    if (!mime || !mime.startsWith("image/")) return;
    const token = localStorage.getItem("ssc_token");
    let revoke = null;
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.ok ? r.blob() : null)
      .then(b => {
        if (!b) return;
        const u = URL.createObjectURL(b);
        revoke = u;
        setBlob(u);
      })
      .catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [url, mime]);
  if (mime && mime.startsWith("image/") && blob) {
    return <img src={blob} alt="" className="w-12 h-12 object-cover rounded border" />;
  }
  return <div className="w-12 h-12 rounded border bg-gray-100 flex items-center justify-center text-[10px] text-gray-500 font-bold">{mime === "application/pdf" ? "PDF" : "FILE"}</div>;
}

function ProtectionTab({ data }) {
  const c = data.customer || {};
  const sel = c.protection_selected || [];
  const dec = c.protection_declined || [];
  const inter = c.protection_interactions || [];
  return (
    <div data-testid="admin-tab-body-protection">
      <Box title="Selected Products">
        {sel.length === 0 && <div className="text-gray-400 text-sm">None added.</div>}
        <ul className="text-sm list-disc ml-5">{sel.map(s => <li key={s.slug || s}>{s.title || s.slug || s}{s.price ? ` — $${s.price}` : ""}</li>)}</ul>
      </Box>
      <Box title="Declined Products">
        {dec.length === 0 && <div className="text-gray-400 text-sm">None declined.</div>}
        <ul className="text-sm list-disc ml-5">{dec.map(s => <li key={s.slug || s}>{s.title || s.slug || s}</li>)}</ul>
      </Box>
      <Box title="Total Protection $"><div className="text-xl font-bold">${c.protection_total || 0}</div></Box>
      {inter.length > 0 && (
        <Box title="Interaction Log">
          <ul className="text-xs text-gray-600 list-disc ml-5">{inter.map((i, n) => <li key={n}>{i.action} · {i.slug} · {(i.at || "").slice(0, 16).replace("T", " ")}</li>)}</ul>
        </Box>
      )}
    </div>
  );
}

function FormsTab({ data, forms }) {
  const c = data.customer || {};
  const open = (key) => {
    const url = `${API}/admin/crm/customer/${c.id}/form/${key}`;
    // Forward auth via cookie; if not set, we still send Bearer via fetch + window.open won't pass it.
    // Solution: fetch with auth, blob, then window.open
    const token = localStorage.getItem("ssc_token");
    if (token) {
      fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.text())
        .then(html => {
          const w = window.open("", "_blank");
          if (w) { w.document.open(); w.document.write(html); w.document.close(); }
        });
    } else {
      window.open(url, "_blank");
    }
  };
  return (
    <div data-testid="admin-tab-body-forms">
      <p className="text-sm text-gray-500 mb-3">Each form auto-fills from this customer's profile. Click to preview, then "Print / Save as PDF" inside the form to generate.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {(forms || []).map(f => (
          <button key={f.key} className="text-left p-3 rounded border border-gray-200 hover:border-[#006DFF] hover:bg-blue-50/30"
            onClick={() => open(f.key)} data-testid={`form-card-${f.key}`}>
            <div className="font-semibold text-[#061426]">{f.title}</div>
            <div className="text-xs text-gray-500 mt-0.5">Open &amp; Print →</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ActivityTab({ data }) {
  return (
    <div data-testid="admin-tab-body-activity">
      <ul className="divide-y">
        {(data.activity || []).map(a => (
          <li key={(a.at || "") + (a.action || "")} className="py-2">
            <div className="text-sm"><span className="font-semibold">{a.action}</span> {a.detail ? <span className="text-gray-600">· {a.detail}</span> : null}</div>
            <div className="text-xs text-gray-400 mt-0.5">{a.user_name || ""} · {(a.at || "").slice(0, 16).replace("T", " ")}</div>
          </li>
        ))}
        {(data.activity || []).length === 0 && <li className="py-4 text-gray-400 text-center">No activity yet.</li>}
      </ul>
    </div>
  );
}

// =============================================================================
// Customer Upload Link panel — generates a /u/{token} mobile portal link
// =============================================================================
const REQUESTABLE_KINDS = [
  { v: "license_front", label: "Driver License (Front)" },
  { v: "license_back",  label: "Driver License (Back)" },
  { v: "insurance",     label: "Insurance Card" },
  { v: "insurance_back", label: "Insurance Card (Back)" },
  { v: "income",        label: "Pay Stub / Income" },
  { v: "address_proof", label: "Proof of Address" },
  { v: "trade_photo",   label: "Trade-In Photo" },
  { v: "title",         label: "Trade Title" },
];

function UploadLinkPanel({ customerId }) {
  const [links, setLinks] = useState([]);
  const [show, setShow] = useState(false);
  const [kinds, setKinds] = useState({ license_front: true, license_back: true, insurance: true });
  const [hours, setHours] = useState(168);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get(`/admin/crm/customer/${customerId}/upload-links`);
    setLinks(r.data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

  const create = async () => {
    const allowed = Object.entries(kinds).filter(([, v]) => v).map(([k]) => k);
    if (!allowed.length) { alert("Pick at least one document to request."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/crm/customer/${customerId}/upload-link`,
        { allowed_kinds: allowed, expires_in_hours: parseInt(hours) || 168, note_to_customer: note, run_ocr: true });
      setShow(false); setNote("");
      await load();
    } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this upload link? The customer won't be able to use it.")) return;
    await api.post(`/admin/crm/customer/${customerId}/upload-link/${id}/revoke`);
    load();
  };

  const copy = (text) => {
    if (navigator.clipboard) navigator.clipboard.writeText(text).then(() => alert("Copied to clipboard."));
    else alert(text);
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3" data-testid="upload-link-panel">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div>
          <div className="font-semibold text-[#061426]">Send customer a phone upload link</div>
          <div className="text-xs text-gray-600">Generate a secure mobile portal — they snap their license &amp; insurance from their phone, you get them here automatically (with OCR).</div>
        </div>
        <button className="ssc-btn-primary text-xs" onClick={() => setShow(s => !s)} data-testid="upload-link-toggle">
          {show ? "Close" : "+ New Upload Link"}
        </button>
      </div>

      {show && (
        <div className="bg-white border rounded p-3 mt-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
            {REQUESTABLE_KINDS.map(k => (
              <label key={k.v} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!kinds[k.v]} onChange={(e) => setKinds(s => ({ ...s, [k.v]: e.target.checked }))}
                  data-testid={`upload-link-kind-${k.v}`} />
                {k.label}
              </label>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
            <div>
              <label className="text-xs text-gray-500">Expires in (hours)</label>
              <input type="number" min={1} max={720} value={hours} onChange={(e) => setHours(e.target.value)}
                className="w-full border border-gray-300 rounded p-2 text-sm" data-testid="upload-link-hours" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-gray-500">Personal note (optional)</label>
              <input value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Hey — please send these whenever you can. Thanks!"
                className="w-full border border-gray-300 rounded p-2 text-sm" data-testid="upload-link-note" />
            </div>
          </div>
          <div className="flex justify-end">
            <button className="ssc-btn-primary" onClick={create} disabled={busy} data-testid="upload-link-create">
              {busy ? "Creating…" : "Create Link"}
            </button>
          </div>
        </div>
      )}

      {links.length > 0 && (
        <ul className="mt-2 divide-y" data-testid="upload-link-list">
          {links.map(l => (
            <li key={l.id} className="py-2" data-testid={`upload-link-row-${l.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs text-gray-700">
                  <span className={`font-bold mr-1 ${l.status === "active" ? "text-emerald-700" : l.status === "completed" ? "text-blue-700" : "text-gray-500"}`}>
                    {l.status.toUpperCase()}
                  </span>
                  · {(l.allowed_kinds || []).length} doc(s) · expires {(l.expires_at || "").slice(0, 16).replace("T", " ")}
                  · {(l.uploads || []).length}/{(l.allowed_kinds || []).length} received
                </div>
                <div className="flex gap-2">
                  {l.share_url && l.status === "active" && (
                    <>
                      <button className="text-xs text-blue-600 hover:underline" onClick={() => copy(l.share_url)} data-testid={`upload-link-copy-${l.id}`}>Copy link</button>
                      <a className="text-xs text-blue-600 hover:underline" href={`sms:?&body=${encodeURIComponent(l.sms_body || l.share_url)}`} data-testid={`upload-link-sms-${l.id}`}>Send via SMS</a>
                      <button className="text-xs text-red-600 hover:underline" onClick={() => revoke(l.id)} data-testid={`upload-link-revoke-${l.id}`}>Revoke</button>
                    </>
                  )}
                </div>
              </div>
              {l.status === "active" && l.share_url && (
                <div className="text-xs font-mono text-gray-500 truncate mt-1">{l.share_url}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}



// =============================================================================
// Layout helpers
// =============================================================================
function Box({ title, children }) {
  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-white">
      <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">{title}</div>
      <div>{children}</div>
    </div>
  );
}
function KV({ label, value }) {
  return (
    <div className="flex justify-between border-b last:border-b-0 py-1 gap-2">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-sm font-medium text-right break-all">{value || "—"}</span>
    </div>
  );
}
function Section({ title, cols = 3, children }) {
  return (
    <div className="mb-3">
      {title && <h4 className="text-xs uppercase tracking-wide text-[#006DFF] font-bold mb-1">{title}</h4>}
      <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-2`}>{children}</div>
    </div>
  );
}
function Field({ label, children, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <label className="text-xs text-gray-500">{label}</label>
      {children}
    </div>
  );
}
